import { type TypeSelectOption } from '@thefarmersfront/kitchen';

import { create } from 'zustand';

import { getDateByOffset } from '~/utils';

import {
  CLAIM_EXCHANGE_TYPE,
  DEFAULT_RETURN_COURIER_NAME,
  INITIAL_CLAIM_PAGE_CURSOR,
  INITIAL_CLAIM_PAGE_SIZE,
} from '~/constants/claim';
import { DEFAULT_INPUT_STATUS, OPTION_ALL } from '~/constants/common';

import type {
  TypeExchangeRegisterSecondStepValues,
  TypeExchangeRegisterSecondStepValuesState,
  TypeRegisterFirstStepSearchFilterValues,
  TypeRegisterFirstStepSearchFilterValuesState,
  TypeReturnRegisterSecondStepValues,
  TypeReturnRegisterSecondStepValuesState,
} from '~/types/claim.types';
import type { TypeInputStatus } from '~/types/common.types';
import type { TypeOrders } from '~/types/order.types';

type TypeClaimStore = TypeClaimStoreState & { actions: TypeClaimStoreActions };

type TypeClaimStoreState = {
  /** 공통 코드 옵션 */
  selectOptions: {
    exchangeKeyword: TypeSelectOption<string>[];
    returnKeyword: TypeSelectOption<string>[];
    returnStatus: TypeSelectOption<string>[];
    orderKeyword: TypeSelectOption<string>[];
    orderDateType: TypeSelectOption<string>[];
    deliveryPolicy: TypeSelectOption<string>[];
    courierCode: TypeSelectOption<string>[];
    clusterCenter: TypeSelectOption<string>[];
    returnPlace: TypeSelectOption<string>[];
    accessMethod: TypeSelectOption<string>[];
    returnReason: TypeSelectOption<string>[];
  };

  /** [반품 신청/조회] 반품 신청 등록 > 1페이지 */
  registerFirstStepSearchFilterValues: TypeRegisterFirstStepSearchFilterValues;
  registerFirstStepSearchFilterValuesStatus: TypeRegisterFirstStepSearchFilterValuesState;
  registerFirstStepOrdersData: TypeOrders[] | null;

  /** [반품 신청/조회] 반품 신청 등록 > 2페이지 */
  returnRegisterSecondStepValues: TypeReturnRegisterSecondStepValues;
  returnRegisterSecondStepValuesState: TypeReturnRegisterSecondStepValuesState;

  /** [교환 신청/조회] 교환 신청 등록 > 2페이지 */
  exchangeRegisterSecondStepValues: TypeExchangeRegisterSecondStepValues;
  exchangeRegisterSecondStepValuesState: TypeExchangeRegisterSecondStepValuesState;
};

type TypeClaimStoreActions = {
  /** 공통 코드 옵션 */
  setSelectOptions: (options: Partial<TypeClaimStore['selectOptions']>) => void;

  /** [반품 교환 관리] 반품 & 교환 신청 등록 > 1페이지 */
  setRegisterFirstStepSearchFilterValues: (value: TypeRegisterFirstStepSearchFilterValues) => void;
  setRegisterFirstStepSearchFilterValuesStatus: (value: TypeRegisterFirstStepSearchFilterValuesState) => void;
  setRegisterFirstStepOrdersData: (value: TypeOrders[] | null) => void;

  /** [반품 신청/조회] 반품 신청 등록 > 2페이지 */
  setReturnRegisterSecondStepValues: (value: TypeReturnRegisterSecondStepValues) => void;
  setReturnRegisterSecondStepValuesState: (value: TypeReturnRegisterSecondStepValuesState) => void;

  /** [교환 신청/조회] 교환 신청 등록 > 2페이지 */
  setExchangeRegisterSecondStepValues: (value: TypeExchangeRegisterSecondStepValues) => void;
  setExchangeRegisterSecondStepValuesState: (value: TypeExchangeRegisterSecondStepValuesState) => void;

  /** 스토어 상태 초기화 */
  resetStore: (keys?: Array<keyof TypeClaimStoreState>) => void;
};

export const initialClaimStoreValue: TypeClaimStoreState = {
  selectOptions: {
    exchangeKeyword: [],
    returnKeyword: [],
    returnStatus: [],
    orderKeyword: [],
    orderDateType: [],
    deliveryPolicy: [],
    courierCode: [],
    clusterCenter: [],
    returnPlace: [],
    accessMethod: [],
    returnReason: [],
  },
  registerFirstStepSearchFilterValues: {
    keywordType: null,
    keyword: '',
    dateType: null,
    deliveryDate: { start: getDateByOffset(1, 'day'), end: getDateByOffset(1, 'day') },
    paymentStartDateTime: null,
    paymentEndDateTime: null,
    deliveryType: OPTION_ALL,
    courier: OPTION_ALL,
    clusterCenter: OPTION_ALL,
    pagination: {
      currentPage: INITIAL_CLAIM_PAGE_CURSOR,
      totalCount: 0,
      pageSize: INITIAL_CLAIM_PAGE_SIZE,
    },
  },
  registerFirstStepSearchFilterValuesStatus: {
    keyword: 'default' as TypeInputStatus,
    deliveryDate: 'default' as TypeInputStatus,
    paymentDateTime: 'default' as TypeInputStatus,
  },
  registerFirstStepOrdersData: null,

  returnRegisterSecondStepValues: {
    outboundCode: '',
    outboundClusterCenter: '',
    outboundCourier: '',
    returnCourier: DEFAULT_RETURN_COURIER_NAME,
    receiverName: '',
    courierMemo: '',
    totalBox: null,
    totalBoxInput: '',
    receiverContactNumber: '',
    primaryAddress: '',
    detailAddress: '',
    returnPlace: null,
    returnPlaceDetail: '',
    accessMethod: null,
    accessDetail: '',
    returnOrderItems: [],
  },
  returnRegisterSecondStepValuesState: {
    totalBox: DEFAULT_INPUT_STATUS,
    totalBoxInput: DEFAULT_INPUT_STATUS,
    receiverContactNumber: DEFAULT_INPUT_STATUS,
    primaryAddress: DEFAULT_INPUT_STATUS,
    returnPlace: DEFAULT_INPUT_STATUS,
    accessMethod: DEFAULT_INPUT_STATUS,
    returnOrderItems: [],
  },

  exchangeRegisterSecondStepValues: {
    returnInfo: {
      outboundCode: '',
      outboundClusterCenter: '',
      outboundCourier: '',
      returnCourier: DEFAULT_RETURN_COURIER_NAME,
      courierMemo: '',
      totalBox: null,
      totalBoxInput: '',
      exchangeType: CLAIM_EXCHANGE_TYPE.NORMAL.value,
      receiverName: '',
      receiverContactNumber: '',
      primaryAddress: '',
      detailAddress: '',
      returnPlace: null,
      returnPlaceDetail: '',
      accessMethod: null,
      accessDetail: '',
    },
    exchangeDeliveryInfo: {
      exchangeCourier: '',
      receiverName: '',
      receiverContactNumber: '',
      primaryAddress: '',
      detailAddress: '',
      receivePlace: null,
      receivePlaceDetail: '',
      accessMethod: null,
      accessDetail: '',
    },
    exchangeOrderItems: [],
  },
  exchangeRegisterSecondStepValuesState: {
    returnInfo: {
      totalBox: DEFAULT_INPUT_STATUS,
      totalBoxInput: DEFAULT_INPUT_STATUS,
      receiverContactNumber: DEFAULT_INPUT_STATUS,
      primaryAddress: DEFAULT_INPUT_STATUS,
      returnPlace: DEFAULT_INPUT_STATUS,
      accessMethod: DEFAULT_INPUT_STATUS,
    },
    exchangeDeliveryInfo: {
      receiverContactNumber: DEFAULT_INPUT_STATUS,
      primaryAddress: DEFAULT_INPUT_STATUS,
      receivePlace: DEFAULT_INPUT_STATUS,
      accessMethod: DEFAULT_INPUT_STATUS,
    },
    exchangeOrderItems: [],
  },
};

/**
 * [반품 교환 관리] 공통 스토어
 */
export const useClaimStore = create<TypeClaimStore>((set) => ({
  ...initialClaimStoreValue,

  actions: {
    setSelectOptions: (options) => set((prev) => ({ selectOptions: { ...prev.selectOptions, ...options } })),

    setRegisterFirstStepSearchFilterValues: (value) => set({ registerFirstStepSearchFilterValues: value }),
    setRegisterFirstStepSearchFilterValuesStatus: (value) => set({ registerFirstStepSearchFilterValuesStatus: value }),
    setRegisterFirstStepOrdersData: (value) => set({ registerFirstStepOrdersData: value }),

    setReturnRegisterSecondStepValues: (value) => set({ returnRegisterSecondStepValues: value }),
    setReturnRegisterSecondStepValuesState: (value) => set({ returnRegisterSecondStepValuesState: value }),

    setExchangeRegisterSecondStepValues: (value) => set({ exchangeRegisterSecondStepValues: value }),
    setExchangeRegisterSecondStepValuesState: (value) => set({ exchangeRegisterSecondStepValuesState: value }),

    resetStore: (keys?: Array<keyof TypeClaimStoreState>) => {
      // 전체 상태 초기화
      if (!keys) {
        set(initialClaimStoreValue);
        return;
      }
      // 일부 상태 초기화
      keys.forEach((key) => {
        set({ [key]: initialClaimStoreValue[key] });
      });
    },
  },
}));
